import { doc, getDoc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore'
import { db } from '../firebase'
import { hasPermission, PERMISSIONS } from './permissionService'

// Session configuration
const SESSION_DURATION = 30 * 60 * 1000 // 30 minutes in milliseconds
const SESSION_WARNING_TIME = 5 * 60 * 1000 // Warn when 5 minutes remaining
const SESSION_COLLECTION = 'replenish_sessions'

/**
 * Create a short-lived replenishment session for a specific machine
 * @param {Object} user - Firebase user object with custom claims
 * @param {Object} userPermissions - User permissions from {customer}_users collection
 * @param {string} machineId - Machine identifier
 * @param {string} customer - Customer identifier
 * @returns {Promise<Object>} Session information or error
 */
export const createReplenishSession = async (user, userPermissions, machineId, customer) => {
  try {
    // Validate user has replenish permission
    if (!hasPermission(user, userPermissions, PERMISSIONS.DEVICES.REPLENISH, customer)) {
      throw new Error('User does not have replenish permission')
    }

    // Check if user has access to this specific machine (device restrictions)
    if (userPermissions?.device_restrictions && userPermissions.device_restrictions.length > 0) {
      if (!userPermissions.device_restrictions.includes(machineId)) {
        throw new Error('User does not have access to this machine')
      }
    }

    const sessionId = `${user.uid}_${machineId}_${Date.now()}`
    const expiresAt = Date.now() + SESSION_DURATION
    const warningAt = expiresAt - SESSION_WARNING_TIME

    const sessionData = {
      sessionId,
      userId: user.email,
      userDisplayName: user.displayName || user.email,
      machineId,
      customer,
      createdAt: new Date(),
      expiresAt: new Date(expiresAt),
      warningAt: new Date(warningAt),
      status: "active",
      permissions: [
        PERMISSIONS.DEVICES.VIEW,
        PERMISSIONS.DEVICES.LAYOUT_EDIT,
        PERMISSIONS.DEVICES.REPLENISH,
        PERMISSIONS.DEVICES.RESTOCK
      ],
      sessionType: 'replenish',
      metadata: {
        userAgent: navigator?.userAgent || 'unknown',
        ipAddress: 'unknown', // Would need server-side implementation for real IP
        qrScanned: true
      }
    }

    // Store session in Firestore
    const sessionRef = doc(db, SESSION_COLLECTION, sessionId)
    await setDoc(sessionRef, sessionData)

    return {
      success: true,
      sessionId,
      expiresAt,
      warningAt,
      durationMinutes: SESSION_DURATION / (60 * 1000),
      machineId
    }
  } catch (error) {
    console.error('Error creating replenish session:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

/**
 * Validate and get active replenishment session
 * @param {string} sessionId - Session identifier
 * @param {string} machineId - Machine identifier (for validation)
 * @returns {Promise<Object>} Session data or null if invalid
 */
export const getActiveSession = async (sessionId, machineId = null) => {
  try {
    const sessionRef = doc(db, SESSION_COLLECTION, sessionId)
    const sessionSnap = await getDoc(sessionRef)

    if (!sessionSnap.exists()) {
      return null
    }

    const sessionData = sessionSnap.data()

    // Check if session is still active
    if (sessionData.status !== "active" || Date.now() > sessionData.expiresAt.toMillis()) {
      // Auto-cleanup expired session
      await deleteDoc(sessionRef)
      return null
    }

    // Validate machine ID if provided
    if (machineId && sessionData.machineId !== machineId) {
      return null
    }

    return sessionData
  } catch (error) {
    console.error('Error getting active session:', error)
    return null
  }
}

/**
 * Extend session expiration time
 * @param {string} sessionId - Session identifier
 * @param {number} additionalMinutes - Additional minutes to extend (default: 15)
 * @returns {Promise<Object>} Updated session info or error
 */
export const extendSession = async (sessionId, additionalMinutes = 15) => {
  try {
    const sessionRef = doc(db, SESSION_COLLECTION, sessionId)
    const sessionData = await getActiveSession(sessionId)

    if (!sessionData) {
      throw new Error('Session not found or expired')
    }

    const additionalTime = additionalMinutes * 60 * 1000
    const newExpiresAt = sessionData.expiresAt.toMillis() + additionalTime
    const newWarningAt = newExpiresAt - SESSION_WARNING_TIME

    await updateDoc(sessionRef, {
      expiresAt: new Date(newExpiresAt),
      warningAt: new Date(newWarningAt),
      extendedAt: new Date(),
      extensionCount: (sessionData.extensionCount || 0) + 1
    })

    return {
      success: true,
      expiresAt: newExpiresAt,
      warningAt: newWarningAt,
      extendedMinutes: additionalMinutes
    }
  } catch (error) {
    console.error('Error extending session:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

/**
 * Terminate a replenishment session
 * @param {string} sessionId - Session identifier
 * @returns {Promise<boolean>} Success status
 */
export const terminateSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, SESSION_COLLECTION, sessionId)
    await deleteDoc(sessionRef)
    return true
  } catch (error) {
    console.error('Error terminating session:', error)
    return false
  }
}

/**
 * Check if session should show warning (5 minutes remaining)
 * @param {Object} sessionData - Session data object
 * @returns {boolean} True if warning should be shown
 */
export const shouldShowWarning = (sessionData) => {
  if (!sessionData || !sessionData.warningAt) {
    return false
  }
  
  const warningTime = sessionData.warningAt?.toMillis ? sessionData.warningAt.toMillis() : sessionData.warningAt
  const expireTime = sessionData.expiresAt?.toMillis ? sessionData.expiresAt.toMillis() : sessionData.expiresAt
  return Date.now() >= warningTime && Date.now() < expireTime
}

/**
 * Get remaining session time in minutes
 * @param {Object} sessionData - Session data object
 * @returns {number} Remaining minutes (0 if expired)
 */
export const getRemainingMinutes = (sessionData) => {
  if (!sessionData || !sessionData.expiresAt) {
    return 0
  }

  const expireTime = sessionData.expiresAt?.toMillis ? sessionData.expiresAt.toMillis() : sessionData.expiresAt
  const remaining = expireTime - Date.now()
  return Math.max(0, Math.ceil(remaining / (60 * 1000)))
}

/**
 * Get human-readable time remaining
 * @param {Object} sessionData - Session data object
 * @returns {string} Formatted time remaining
 */
export const getTimeRemaining = (sessionData) => {
  const minutes = getRemainingMinutes(sessionData)
  
  if (minutes === 0) {
    return 'Expired'
  } else if (minutes === 1) {
    return '1 minute'
  } else if (minutes < 60) {
    return `${minutes} minutes`
  } else {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }
}

/**
 * Clean up expired sessions (should be called periodically)
 * @param {string} customer - Customer identifier
 * @returns {Promise<number>} Number of cleaned up sessions
 */
export const cleanupExpiredSessions = async (customer) => {
  try {
    // This would ideally be done with a server-side function
    // For now, we'll implement basic cleanup on client side
    console.log('Session cleanup would be handled by server-side functions')
    return 0
  } catch (error) {
    console.error('Error cleaning up expired sessions:', error)
    return 0
  }
}

/**
 * Validate that current user can access machine in session
 * @param {Object} user - Firebase user object
 * @param {Object} userPermissions - User permissions
 * @param {Object} sessionData - Session data
 * @returns {boolean} True if user can access the session
 */
export const validateSessionAccess = (user, userPermissions, sessionData) => {
  if (!user || !sessionData) {
    return false
  }

  // Session must belong to this user
  if (sessionData.userId !== user.email) {
    return false
  }

  // User must still have replenish permission - check directly from userPermissions
  if (!userPermissions?.permissions?.includes(PERMISSIONS.DEVICES.REPLENISH)) {
    return false
  }

  // Check device restrictions if they exist
  if (userPermissions?.device_restrictions && userPermissions.device_restrictions.length > 0) {
    if (!userPermissions.device_restrictions.includes(sessionData.machineId)) {
      return false
    }
  }

  return true
}